// ./src/hooks/useRsvpQuestions.ts
//
// Loads an event's registration questions (active + disabled, so the management
// page can re-enable disabled ones), ordered. `reload` refetches after a change.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { RsvpQuestionService, RsvpQuestion } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseRsvpQuestionsReturn {
  questions: RsvpQuestion[];
  loading: boolean;
  /** True once the first fetch has completed (list may still be empty). */
  isLoaded: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
}

/**
 * ⚠ `askedOn` is REQUIRED. One engine backs two forms (the RSVP form and the partner-application
 * form) off one table, so a hook that omits it mixes both sets into whichever editor is open.
 */
export const useRsvpQuestions = (eventId: string | null | undefined, askedOn: string): UseRsvpQuestionsReturn => {
  const [questions, setQuestions] = useState<RsvpQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);
  // Whose questions are held — a failed REFETCH of the same form keeps them (#126 review): every caller shows the error
  // in place of the list anyway, and clearing them closed an open edit drawer (its question resolved to nothing).
  const heldForRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) {
      heldForRef.current = null;
      setQuestions([]);
      setIsLoaded(true);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await RsvpQuestionService.list(eventId, askedOn, [STATUSES.ACTIVE, STATUSES.DISABLED]);

    if (myCall !== callIdRef.current) return;
    setLoading(false);
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setQuestions(result.data as RsvpQuestion[]);
      heldForRef.current = `${eventId}:${askedOn}`;
    } else {
      setError(result);
      if (heldForRef.current !== `${eventId}:${askedOn}`) {
        setQuestions([]);
      }
    }
  }, [eventId, askedOn]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    load();
  }, [load]);

  return { questions, loading, isLoaded, error, reload: load };
};
